import React, { useState, useEffect, useRef } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  EyeIcon, 
  EyeOffIcon, 
  MenuAlt4Icon,
  ClipboardListIcon,
  DotsVerticalIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/outline';
import DropdownPortal from './DropdownPortal';
import DeleteConfirmModal from '../../../../../../components/common/DeleteConfirmModal';
import PermissionGuard from '../../../../../common/PermissionGuard';

const Quiz = ({ quiz, onStatusChange, onDelete, onEdit }) => {
  const [showActions, setShowActions] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const buttonRef = useRef(null);
  
  // إغلاق القائمة عند النقر خارجها
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showActions && !event.target.closest('.dropdown-menu')) {
        setShowActions(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showActions]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: quiz.id,
    transition: {
      duration: 150, // تسريع مدة الانتقال
      easing: 'cubic-bezier(0.25, 1, 0.5, 1)' // منحنى حركة أكثر سلاسة
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <React.Fragment>
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        className={`relative flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg
          border border-gray-200 dark:border-gray-700 shadow-sm cursor-default overflow-visible
          ${isDragging ? 'opacity-50 shadow-lg scale-105' : ''}`}
      >
        <div className="flex items-center gap-3 overflow-visible">
          {/* مقبض السحب */}
          <div className="relative group overflow-visible flex items-center">
            <div
              {...listeners}
              className="cursor-move text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <MenuAlt4Icon className="w-4 h-4" />
            </div>
            <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
              اسحب لإعادة الترتيب
            </div>
          </div>

          {/* أيقونة نوع المحتوى */}
          <div className="relative group overflow-visible flex items-center">
            <ClipboardListIcon className="w-5 h-5 text-purple-500 dark:text-purple-400" />
            <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
              اختبار
            </div>
          </div>

          <div className="relative group overflow-visible flex items-center">
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {quiz.title}
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {quiz.questionsCount} أسئلة • {quiz.duration} دقيقة
              </p>
            </div>
            <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
              عرض تفاصيل الاختبار
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 overflow-visible">
          {/* زر المعاينة */}
          <div className="relative group overflow-visible flex items-center">
            <button
              onClick={(e) => {
                e.stopPropagation();
                window.open(`/admin/dashboard/courses/exams/preview/${quiz.id}`, '_blank');
              }}
              className="text-blue-500 hover:text-blue-700"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
              </svg>
            </button>
            <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
              معاينة الاختبار
            </div>
          </div>

          {/* زر التعديل */}
          <PermissionGuard permission="edit_course_content">
            <div className="relative group overflow-visible flex items-center">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit && onEdit(quiz.id);
                }}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
              >
                <PencilIcon className="w-4 h-4" />
              </button>
              <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
                تعديل الاختبار
              </div>
            </div>
          </PermissionGuard>

          {/* مؤشر حالة النشر */}
          <div className="relative group overflow-visible flex items-center">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onStatusChange && onStatusChange(quiz.id);
              }}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
            >
              {quiz.status === 'published' ? (
                <EyeIcon className="w-4 h-4 text-green-500" />
              ) : (
                <EyeOffIcon className="w-4 h-4" />
              )}
            </button>
            <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
              {quiz.status === 'published' ? 'إخفاء الاختبار' : 'نشر الاختبار'}
            </div>
          </div>

          {/* زر الحذف */}
          <PermissionGuard permission="delete_course_content">
            <div className="relative group overflow-visible flex items-center">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteModal(true);
                }}
                className="text-red-400 dark:text-red-500 hover:text-red-600 dark:hover:text-red-400"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
              <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
                حذف الاختبار
              </div>
            </div>
          </PermissionGuard>
        </div>
      </div>
      
      {/* مودال تأكيد الحذف */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => {
          // تأكيد حذف الاختبار
          onDelete && onDelete(quiz.id);
          setShowDeleteModal(false);
        }}
        itemType="الاختبار"
        itemTitle={quiz.title}
      />
    </React.Fragment>
  );
};

export default Quiz;
